// Loading.js
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { LeftSide } from '../../components/LeftSide/LeftSide';
import { Navi } from '../../components/Navi/Navi';
import { RightBar } from '../../components/RightBar/RightBar';
import { toKST } from '../../utils/date';
import '../../styles/common.css'; // 공통 CSS 파일 import
import '../Users/User.css';
import AuthService from '../../services/authService';
import api from '../../utils/api'; // 새로운 API 인스턴스 불러오기
import { toast } from 'react-toastify';
import { useMinLoadingDisplay } from '../../hooks/useMinLoadingDisplay';
import { ScrollToTop } from '../../components/ScrollToTop/ScrollToTop';
import WireTransferList from './WireTransferList';

const PAGE_SIZE_KEY = 'admin_settlement_pageSize';

const Settlement = ({ title = '정산관리' }) => {
    const [activeTab, setActiveTab] = useState('normal');
    const [pendingCount, setPendingCount] = useState(0);
    // 정산 관리 관련 상태
    const [histories, setHistories] = useState([]);
    const [filteredHistories, setFilteredHistories] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(() => {
        const saved = localStorage.getItem(PAGE_SIZE_KEY);
        return saved ? parseInt(saved, 10) : 50;
    });
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortOrder, setSortOrder] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const showListLoading = useMinLoadingDisplay(isLoading);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedHistory, setSelectedHistory] = useState(null);
    const [memo, setMemo] = useState('');
    const [showMemoModal, setShowMemoModal] = useState(false);
    const [selectedMemo, setSelectedMemo] = useState('');
    const [showDetailModal, setShowDetailModal] = useState(false);
    
    // 환불 관련 상태
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [refundAmount, setRefundAmount] = useState('');
    const [refundReason, setRefundReason] = useState('');

    // 정산 내역 데이터 가져오기
    const fetchHistories = async (
        page = currentPage,
        search = searchTerm,
        status = statusFilter,
        sort = sortOrder,
        limit = itemsPerPage
    ) => {
        setIsLoading(true);
        try {
            const response = await api.get('/settlement/histories', {
                params: {
                    page: page,
                    limit: limit,
                    search: search,
                    status: status,
                    sort: sort,
                },
            });

            if (response.data.success) {
                setHistories(response.data.histories);
                setFilteredHistories(response.data.histories);
                setTotalPages(Math.ceil(response.data.total / limit));
                
                // 대기 건수 조회 (별도 API 호출 대신 탭 전환 시점마다 갱신 용도)
                const countRes = await api.get('/settlement/wire-transfers/pending-count');
                if (countRes.data && countRes.data.success) {
                    setPendingCount(countRes.data.count);
                }
            } else {
                toast.error(
                    response.data.message ||
                        '정산 내역을 불러오지 못했습니다.'
                );
                console.error('데이터를 불러오지 못했습니다:', response.data.message);
            }
        } catch (err) {
            if (err.response && err.response.data && err.response.data.message) {
                toast.error(err.response.data.message);
            } else {
                toast.error('정산 내역을 불러오는 중 오류가 발생했습니다.');
            }
            console.error('정산 내역 데이터 가져오기 실패:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // 정산 승인 함수
    const confirmHistory = async historyId => {
        try {
            const response = await api.post(`/settlement/histories/${historyId}/confirm`, {
                memo: memo,
            });
            if (response.data.success) {
                toast.success('정산이 처리되었습니다.');
                fetchHistories();
                setShowConfirmModal(false);
                setMemo(''); // 메모 초기화
                return true;
            }
            toast.error(
                response.data?.message || '정산 처리에 실패했습니다.'
            );
            return false;
        } catch (err) {
            if (err.response && err.response.data && err.response.data.message) {
                toast.error(err.response.data.message);
            } else {
                toast.error('정산 처리 중 오류가 발생했습니다.');
            }
            console.error('정산 처리 실패:', err);
            return false;
        }
    };

    // 임시 삭제 처리 함수
    const handleDelete = async historyId => {
        if (window.confirm('정말 이 내역을 삭제하시겠습니까? (테스트용)')) {
            try {
                const response = await api.delete(`/settlement/histories/${historyId}`);
                if (response.data.success) {
                    toast.success('내역이 삭제되었습니다.');
                    fetchHistories();
                } else {
                    toast.error(response.data.message || '삭제에 실패했습니다.');
                }
            } catch (error) {
                console.error('삭제 오류:', error);
                toast.error('삭제 처리 중 오류가 발생했습니다.');
            }
        }
    };

    // 환불 처리 함수
    const processRefund = async historyId => {
        if (!refundReason.trim()) {
            toast.error('환불 사유를 입력해주세요.');
            return false;
        }

        try {
            const response = await api.post(`/settlement/histories/${historyId}/refund`, {
                cancelReason: refundReason,
                cancelAmount: refundAmount ? Number(refundAmount) : null
            });
            if (response.data.success) {
                toast.success('결제 취소(환불)가 처리되었습니다.');
                fetchHistories();
                setShowRefundModal(false);
                setRefundAmount('');
                setRefundReason('');
                return true;
            }
            toast.error(
                response.data?.message || '결제 취소 처리에 실패했습니다.'
            );
            return false;
        } catch (err) {
            if (err.response && err.response.data && err.response.data.message) {
                toast.error(err.response.data.message);
            } else {
                toast.error('결제 취소 처리 중 오류가 발생했습니다.');
            }
            console.error('결제 취소 처리 실패:', err);
            return false;
        }
    };

    // 현재 페이지의 정산 내역 데이터
    const getCurrentHistories = () => {
        return filteredHistories;
    };

    // 페이지 변경 핸들러
    const handlePageChange = pageNumber => {
        setCurrentPage(pageNumber);
    };

    // 검색어 변경 핸들러
    const handleSearch = e => {
        const value = e.target.value;
        setSearchTerm(value);
    };

    // 상태 필터 변경 핸들러
    const handleStatusFilter = e => {
        const value = e.target.value;
        setStatusFilter(value);
        setCurrentPage(1);
    };

    // 정렬 순서 변경 핸들러
    const handleSortOrder = e => {
        const value = e.target.value;
        setSortOrder(value);
        setCurrentPage(1);
    };

    const handleItemsPerPageChange = e => {
        const val = parseInt(e.target.value, 10);
        setItemsPerPage(val);
        localStorage.setItem(PAGE_SIZE_KEY, val);
        setCurrentPage(1);
        fetchHistories(1, searchTerm, statusFilter, sortOrder, val);
    };

    // 검색 조건 초기화 함수
    const resetSearch = () => {
        setSearchTerm('');
        setStatusFilter('pending');
        setSortOrder('');
        setCurrentPage(1);
        fetchHistories(1, '', 'pending', '');
    };

    // 엔터 키 검색 핸들러
    const handleKeyPress = e => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setCurrentPage(1);
            fetchHistories(1, searchTerm, statusFilter, sortOrder);
        }
    };

    // 정산 처리 모달 열기
    const handleConfirmClick = history => {
        setSelectedHistory(history);
        setMemo(''); // 메모 초기화
        setShowConfirmModal(true);
    };

    // 메모 확인 모달 열기
    const handleMemoClick = memo => {
        setSelectedMemo(memo);
        setShowMemoModal(true);
    };

    // 상세 내용 모달 열기
    const handleDetailClick = history => {
        setSelectedHistory(history);
        setShowDetailModal(true);
    };

    // 환불 모달 열기
    const handleRefundClick = history => {
        setSelectedHistory(history);
        setRefundAmount(history.payment_amount || '');
        setRefundReason('');
        setShowRefundModal(true);
    };

    useEffect(() => {
        if (activeTab === 'normal') {
            fetchHistories();
        }
    }, [currentPage, activeTab]);

    // 페이지네이션 렌더링
    const renderPagination = () => {
        const pageNumbers = [];
        let startPage = Math.max(1, currentPage - 5);
        let endPage = Math.min(totalPages, startPage + 9);

        if (endPage - startPage < 9 && totalPages > 10) {
            startPage = Math.max(1, endPage - 9);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <nav aria-label="정산 내역 페이지네이션">
                <ul className="pagination pagination-sm justify-content-center mt-3">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <Link
                            className="page-link"
                            onClick={e => {
                                e.preventDefault();
                                handlePageChange(1);
                            }}
                            title="맨 앞"
                        >
                            <i className="fas fa-angle-double-left"></i>
                        </Link>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <Link
                            className="page-link"
                            onClick={e => {
                                e.preventDefault();
                                if (currentPage > 1) handlePageChange(currentPage - 1);
                            }}
                        >
                            이전
                        </Link>
                    </li>
                    {pageNumbers.map(number => (
                        <li
                            key={number}
                            className={`page-item ${currentPage === number ? 'active' : ''}`}
                        >
                            <Link
                                className="page-link"
                                onClick={e => {
                                    e.preventDefault();
                                    handlePageChange(number);
                                }}
                            >
                                {number}
                            </Link>
                        </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <Link
                            className="page-link"
                            onClick={e => {
                                e.preventDefault();
                                if (currentPage < totalPages) handlePageChange(currentPage + 1);
                            }}
                        >
                            다음
                        </Link>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <Link
                            className="page-link"
                            onClick={e => {
                                e.preventDefault();
                                handlePageChange(totalPages);
                            }}
                            title="맨 뒤"
                        >
                            <i className="fas fa-angle-double-right"></i>
                        </Link>
                    </li>
                </ul>
            </nav>
        );
    };

    return (
        <>
            {/* <!-- Begin page --> */}
            <div id="wrapper">
                <Navi title={title} />

                <LeftSide />

                <div className="content-page">
                    <div className="content">
                        {/* <!-- Start Content--> */}
                        <div className="container-fluid users-admin-page">
                            <div className="row">
                                <div className="col-xl-12">
                                    <div className="card">
                                        <div className="card-body">
                                            {/* 탭 메뉴 */}
                                            <ul className="nav nav-tabs mb-2" style={{ borderBottom: '1px solid #e8eaef' }}>
                                                <li className="nav-item">
                                                    <button 
                                                        className={`nav-link ${activeTab === 'normal' ? 'active' : ''}`} 
                                                        onClick={() => setActiveTab('normal')}
                                                        style={{
                                                            border: activeTab === 'normal' ? '1px solid #e8eaef' : '1px solid transparent',
                                                            borderBottomColor: activeTab === 'normal' ? '#fff' : 'transparent',
                                                            color: activeTab === 'normal' ? '#2e5bff' : '#64748b',
                                                            fontWeight: activeTab === 'normal' ? '600' : '500',
                                                            marginBottom: '-1px',
                                                            backgroundColor: activeTab === 'normal' ? '#fff' : 'transparent',
                                                            borderRadius: '6px 6px 0 0'
                                                        }}
                                                    >
                                                        일반 정산 내역
                                                    </button>
                                                </li>
                                                <li className="nav-item">
                                                    <button 
                                                        className={`nav-link ${activeTab === 'wire' ? 'active' : ''}`} 
                                                        onClick={() => setActiveTab('wire')}
                                                        style={{
                                                            border: activeTab === 'wire' ? '1px solid #e8eaef' : '1px solid transparent',
                                                            borderBottomColor: activeTab === 'wire' ? '#fff' : 'transparent',
                                                            color: activeTab === 'wire' ? '#2e5bff' : '#64748b',
                                                            fontWeight: activeTab === 'wire' ? '600' : '500',
                                                            marginBottom: '-1px',
                                                            backgroundColor: activeTab === 'wire' ? '#fff' : 'transparent',
                                                            borderRadius: '6px 6px 0 0'
                                                        }}
                                                    >
                                                        무통장 입금 내역 
                                                        {pendingCount > 0 && (
                                                            <span 
                                                                className="badge bg-danger ms-2 shadow-sm" 
                                                                style={{ 
                                                                    display: 'inline-flex', 
                                                                    alignItems: 'center', 
                                                                    justifyContent: 'center', 
                                                                    width: pendingCount > 9 ? 'auto' : '18px', 
                                                                    minWidth: '18px',
                                                                    height: '18px', 
                                                                    borderRadius: '9px', 
                                                                    padding: pendingCount > 9 ? '0 5px' : '0',
                                                                    fontSize: '11px',
                                                                    fontWeight: 'bold',
                                                                    lineHeight: '1'
                                                                }}
                                                            >
                                                                {pendingCount}
                                                            </span>
                                                        )}
                                                    </button>
                                                </li>
                                            </ul>

                                            {activeTab === 'normal' && (
                                            <>
                                            {/* 검색 및 필터링 영역 */}
                                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-3">
                                                {/* 왼쪽: 필터, 정렬, 검색 */}
                                                <div className="d-flex flex-wrap align-items-center gap-1">
                                                    <select
                                                        className="form-select form-select-sm"
                                                        aria-label="상태 필터"
                                                        value={statusFilter}
                                                        onChange={handleStatusFilter}
                                                        style={{ width: '160px' }}
                                                    >
                                                        <option value="waiting">처리대기</option>
                                                        <option value="completed">처리완료</option>
                                                        <option value="">모든 상태</option>
                                                    </select>

                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="사용자 검색"
                                                        value={searchTerm}
                                                        onChange={handleSearch}
                                                        onKeyPress={handleKeyPress}
                                                        style={{ width: '200px' }}
                                                    />

                                                    <button
                                                        className="btn btn-primary btn-sm"
                                                        type="button"
                                                        onClick={() => {
                                                            setCurrentPage(1);
                                                            fetchHistories(
                                                                1,
                                                                searchTerm,
                                                                statusFilter,
                                                                sortOrder
                                                            );
                                                        }}
                                                    >
                                                        <i className="fas fa-search"></i> 검색
                                                    </button>
                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        type="button"
                                                        onClick={resetSearch}
                                                    >
                                                        <i className="fas fa-sync-alt"></i> 초기화
                                                    </button>
                                                </div>
                                                <div className="d-flex align-items-center gap-1">
                                                    <span className="text-muted" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>페이지당</span>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={itemsPerPage}
                                                        onChange={handleItemsPerPageChange}
                                                        style={{ width: 80 }}
                                                    >
                                                        {[50, 100, 250, 500, 1000].map(n => (
                                                            <option key={n} value={n}>{n}개</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {/* 정산 처리 모달 */}
                                            {showConfirmModal && selectedHistory && (
                                                <>
                                                    <div
                                                        className="modal fade show"
                                                        id="confirmModal"
                                                        tabIndex="-1"
                                                        aria-labelledby="confirmModalLabel"
                                                        aria-modal="true"
                                                        role="dialog"
                                                        style={{ display: 'block' }}
                                                        onClick={() => setShowConfirmModal(false)}
                                                    >
                                                        <div className="modal-dialog modal-dialog-centered admin-form-modal" onClick={e => e.stopPropagation()}>
                                                            <div className="modal-content">
                                                                <div className="modal-header">
                                                                    <h5
                                                                        className="modal-title"
                                                                        id="confirmModalLabel"
                                                                    >
                                                                        정산 처리
                                                                    </h5>
                                                                    <button
                                                                        type="button"
                                                                        className="btn-close"
                                                                        data-bs-dismiss="modal"
                                                                        aria-label="Close"
                                                                        onClick={() =>
                                                                            setShowConfirmModal(
                                                                                false
                                                                            )
                                                                        }
                                                                    ></button>
                                                                </div>
                                                                <div className="modal-body">
                                                                    <div className="mb-3">
                                                                        <label className="form-label">
                                                                            사용자
                                                                        </label>
                                                                        <div className="form-control-plaintext">
                                                                            {
                                                                                selectedHistory.user_name
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                    <div className="mb-3">
                                                                        <label className="form-label">
                                                                            이전 만료일
                                                                        </label>
                                                                        <div className="form-control-plaintext">
                                                                            {toKST(
                                                                                selectedHistory.previous_end_date
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="mb-3">
                                                                        <label className="form-label">
                                                                            새 만료일
                                                                        </label>
                                                                        <div className="form-control-plaintext">
                                                                            {toKST(
                                                                                selectedHistory.new_end_date
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="mb-3">
                                                                        <label className="form-label">
                                                                            추가 일수
                                                                        </label>
                                                                        <div className="form-control-plaintext">
                                                                            {
                                                                                selectedHistory.days_added
                                                                            }
                                                                            일
                                                                        </div>
                                                                    </div>
                                                                    <div className="mb-3">
                                                                        <label className="form-label">
                                                                            사유
                                                                        </label>
                                                                        <div className="form-control-plaintext">
                                                                            {selectedHistory.reason ||
                                                                                '-'}
                                                                        </div>
                                                                    </div>
                                                                    <div className="mb-3">
                                                                        <label className="form-label">
                                                                            처리 관리자
                                                                        </label>
                                                                        <div className="form-control-plaintext">
                                                                            {
                                                                                selectedHistory.changed_by_name
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                    <div className="mb-3">
                                                                        <label className="form-label">
                                                                            처리일시
                                                                        </label>
                                                                        <div className="form-control-plaintext">
                                                                            {toKST(
                                                                                selectedHistory.created_at
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="mb-3">
                                                                        <label className="form-label">
                                                                            메모
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            rows="3"
                                                                            value={memo}
                                                                            onChange={e =>
                                                                                setMemo(
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            placeholder="메모를 입력하세요"
                                                                        ></textarea>
                                                                    </div>
                                                                </div>
                                                                <div className="modal-footer">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-secondary"
                                                                        onClick={() =>
                                                                            setShowConfirmModal(
                                                                                false
                                                                            )
                                                                        }
                                                                    >
                                                                        닫기
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary"
                                                                        onClick={() =>
                                                                            confirmHistory(
                                                                                selectedHistory.id
                                                                            )
                                                                        }
                                                                    >
                                                                        승인
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="modal-backdrop fade show ant-like-modal-backdrop"></div>
                                                </>
                                            )}

                                            {/* 정산 내역 테이블 */}
                                            <div
                                                className="table-responsive"
                                                style={{ overflowX: 'auto' }}
                                            >
                                                {showListLoading ? (
                                                    <div className="text-center p-4">
                                                        <div
                                                            className="loading-spinner loading-spinner-lg"
                                                            role="status"
                                                            aria-live="polite"
                                                        >
                                                            <span className="visually-hidden">
                                                                데이터를 불러오는 중입니다.
                                                            </span>
                                                        </div>
                                                        <p className="mt-2">
                                                            데이터를 불러오는 중입니다...
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <table className="table table-bordered table-hover table-sm align-middle text-nowrap">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th className="text-center">
                                                                    번호
                                                                </th>
                                                                <th className="text-center">
                                                                    사용자 아이디
                                                                </th>
                                                                <th className="text-center">
                                                                    처리 관리자
                                                                </th>
                                                                <th className="text-center">
                                                                    유형
                                                                </th>
                                                                <th className="text-center">
                                                                    결제 금액
                                                                </th>
                                                                <th className="text-center">
                                                                    영수증
                                                                </th>
                                                                <th className="text-center">
                                                                    등급 변경
                                                                </th>
                                                                <th className="text-center">
                                                                    처리일시
                                                                </th>
                                                                <th className="text-center">
                                                                    이전만료일
                                                                </th>
                                                                <th className="text-center">
                                                                    신규만료일
                                                                </th>
                                                                <th className="text-center">
                                                                    메모
                                                                </th>
                                                                <th className="text-center">
                                                                    상태
                                                                </th>
                                                                <th className="text-center">
                                                                    관리
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {getCurrentHistories().map(history => (
                                                                <tr key={history.id}>
                                                                    <td className="text-center">
                                                                        {history.id}
                                                                    </td>
                                                                    <td className="text-center users-admin-col-id">
                                                                        {history.user_id} (
                                                                        {history.user_name})
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {history.confirmed_by_name || history.changed_by_name || '관리자'}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span
                                                                            className={`badge rounded-pill ${
                                                                                !history.reason ? 'bg-secondary' :
                                                                                (history.reason.includes('거절') || history.reason.includes('환불') || history.reason.includes('취소') || history.reason.includes('실패')) ? 'bg-danger' :
                                                                                (history.reason.includes('자동 승인') || history.reason.includes('연장') || history.reason.includes('신규 가입')) ? 'bg-success' :
                                                                                (history.reason.includes('단건 결제') || history.reason.includes('승인')) ? 'bg-primary' :
                                                                                history.reason.includes('확인 요망') ? 'bg-warning text-dark' :
                                                                                'bg-secondary'
                                                                            }`}
                                                                            style={{ padding: '0.5rem 0.8rem', fontSize: '0.85rem', fontWeight: '500' }}
                                                                        >
                                                                            {history.reason || '-'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {history.payment_amount ? `${Number(history.payment_amount).toLocaleString()}원` : '-'}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {history.receipt_url ? (
                                                                            <button 
                                                                                type="button" 
                                                                                onClick={() => window.open(history.receipt_url, 'receiptPopup', 'width=450,height=700,left=200,top=100,scrollbars=yes')} 
                                                                                className="btn btn-sm btn-outline-secondary"
                                                                                style={{ padding: '2px 8px', fontSize: '12px' }}
                                                                            >
                                                                                보기
                                                                            </button>
                                                                        ) : '-'}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {history.grade_from && history.grade_to
                                                                            ? `${history.grade_from} → ${history.grade_to}`
                                                                            : '-'}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {toKST(history.created_at)}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {toKST(
                                                                            history.previous_end_date
                                                                        )}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {toKST(
                                                                            history.new_end_date
                                                                        )}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {history.memo ? (
                                                                            <button
                                                                                className="btn btn-sm btn-link p-0"
                                                                                onClick={() =>
                                                                                    handleMemoClick(
                                                                                        history.memo
                                                                                    )
                                                                                }
                                                                                title="메모 보기"
                                                                            >
                                                                                <i className="fas fa-sticky-note" style={{ fontSize: '20px', color: '#ffc107', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}></i>
                                                                            </button>
                                                                        ) : (
                                                                            ''
                                                                        )}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span
                                                                            className={`badge ${history.is_confirmed ? 'bg-success' : 'bg-warning'} p-2`}
                                                                        >
                                                                            {history.is_confirmed
                                                                                ? '처리완료'
                                                                                : '처리대기'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {history.is_confirmed ? (
                                                                            <div className="d-flex justify-content-center gap-1">
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    onClick={() =>
                                                                                        handleDetailClick(
                                                                                            history
                                                                                        )
                                                                                    }
                                                                                    title="상세보기"
                                                                                >
                                                                                    <i className="fas fa-eye me-1"></i>상세
                                                                                </button>
                                                                                {(!history.reason?.includes('환불') && !history.reason?.includes('취소') && (history.payment_amount || history.reason === '무통장 입금 승인')) && (
                                                                                    <button
                                                                                        className="btn btn-sm btn-danger"
                                                                                        onClick={() =>
                                                                                            handleRefundClick(
                                                                                                history
                                                                                            )
                                                                                        }
                                                                                        title="결제 취소(환불)"
                                                                                    >
                                                                                        <i className="fas fa-undo-alt me-1"></i>환불
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="d-flex justify-content-center gap-1">
                                                                                <button
                                                                                    className="btn btn-sm btn-primary"
                                                                                    onClick={() =>
                                                                                        handleConfirmClick(
                                                                                            history
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <i className="fas fa-check me-1"></i>승인
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </div>

                                            {/* 페이지네이션 */}
                                            {!showListLoading && renderPagination()}
                                            </>
                                            )}

                                            {activeTab === 'wire' && <WireTransferList />}
                                        </div>
                                    </div>
                                </div>
                                {/* <!-- end col --> */}
                            </div>
                            {/* <!-- end row --> */}
                        </div>
                        {/* <!-- container-fluid --> */}
                    </div>
                    {/* <!-- content --> */}
                </div>
            </div>

            <RightBar />
            <div className="rightbar-overlay"></div>
            <ScrollToTop />

            {/* 메모 확인 모달 */}
            {showMemoModal && (
                <>
                    <div
                        className="modal fade show"
                        id="memoModal"
                        tabIndex="-1"
                        aria-labelledby="memoModalLabel"
                        aria-modal="true"
                        role="dialog"
                        style={{ display: 'block' }}
                        onClick={() => setShowMemoModal(false)}
                    >
                        <div className="modal-dialog modal-dialog-centered admin-form-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title" id="memoModalLabel">
                                        메모 내용
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        data-bs-dismiss="modal"
                                        aria-label="Close"
                                        onClick={() => setShowMemoModal(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <div className="form-control-plaintext">{selectedMemo}</div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowMemoModal(false)}
                                    >
                                        닫기
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show ant-like-modal-backdrop"></div>
                </>
            )}

            {/* 환불 처리 모달 */}
            {showRefundModal && selectedHistory && (
                <>
                    <div
                        className="modal fade show"
                        id="refundModal"
                        tabIndex="-1"
                        aria-labelledby="refundModalLabel"
                        aria-modal="true"
                        role="dialog"
                        style={{ display: 'block' }}
                        onClick={() => setShowRefundModal(false)}
                    >
                        <div className="modal-dialog modal-dialog-centered admin-form-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title text-danger" id="refundModalLabel">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        {selectedHistory?.reason === '무통장 입금 승인' ? '무통장 입금 승인 취소' : '결제 취소 및 환불'}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        data-bs-dismiss="modal"
                                        aria-label="Close"
                                        onClick={() => setShowRefundModal(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="alert alert-warning p-2 mb-3 text-dark" style={{ fontSize: '13px' }}>
                                        {selectedHistory?.reason === '무통장 입금 승인' ? (
                                            <>주의: 승인 취소 시 유저의 권한이 <strong>즉시 강등</strong>되며, 무통장 입금 신청이 다시 <strong>대기 상태</strong>로 돌아갑니다.</>
                                        ) : (
                                            <>주의: 결제 취소 시 유저의 프리미엄 등급이 <strong>즉시 강등</strong>되며, 정기 결제가 해지됩니다.</>
                                        )}
                                    </div>
                                    {selectedHistory?.reason !== '무통장 입금 승인' && (
                                        <>
                                            <div className="mb-3">
                                                <label className="form-label">총 결제 금액</label>
                                                <div className="form-control-plaintext fw-bold">
                                                    {Number(selectedHistory?.payment_amount || 0).toLocaleString()}원
                                                </div>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">취소(환불)할 금액</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={refundAmount}
                                                    onChange={e => setRefundAmount(e.target.value)}
                                                    placeholder="예: 25000 (입력 안 하면 전액 취소)"
                                                    max={selectedHistory?.payment_amount}
                                                />
                                                <small className="text-muted">부분 환불을 원하시면 사용할 일수를 제외한 나머지 금액만 입력하세요.</small>
                                            </div>
                                        </>
                                    )}
                                    <div className="mb-3">
                                        <label className="form-label">취소 사유 <span className="text-danger">*</span></label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={refundReason}
                                            onChange={e => setRefundReason(e.target.value)}
                                            placeholder={selectedHistory?.reason === '무통장 입금 승인' ? "예: 잘못된 승인 처리로 인한 취소" : "예: 며칠 사용 후 변심 환불"}
                                        ></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowRefundModal(false)}
                                    >
                                        닫기
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={() => processRefund(selectedHistory.id)}
                                    >
                                        {selectedHistory?.reason === '무통장 입금 승인' ? '취소 실행' : '환불 실행'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show ant-like-modal-backdrop"></div>
                </>
            )}

            {/* 상세 내용 모달 */}
            {showDetailModal && selectedHistory && (
                <>
                    <div
                        className="modal fade show"
                        id="detailModal"
                        tabIndex="-1"
                        aria-labelledby="detailModalLabel"
                        aria-modal="true"
                        role="dialog"
                        style={{ display: 'block' }}
                        onClick={() => setShowDetailModal(false)}
                    >
                        <div className="modal-dialog modal-lg modal-dialog-centered admin-form-modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title" id="detailModalLabel">
                                        정산 상세 내역
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        data-bs-dismiss="modal"
                                        aria-label="Close"
                                        onClick={() => setShowDetailModal(false)}
                                    ></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="row g-4">
                                        <div className="col-md-6">
                                            <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>사용자</label>
                                            <div className="fw-bold" style={{ fontSize: '15px' }}>{selectedHistory.user_id} ({selectedHistory.user_name})</div>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>유형</label>
                                            <div className={`fw-bold ${
                                                    selectedHistory.reason === '프리미엄 할당' || selectedHistory.reason?.includes('신규 가입')
                                                        ? 'text-primary'
                                                        : selectedHistory.reason?.includes('환불') || selectedHistory.reason === '프리미엄 만료 자동 다운그레이드'
                                                          ? 'text-danger'
                                                          : 'text-secondary'
                                                }`} style={{ fontSize: '15px' }}>
                                                {selectedHistory.reason || '-'}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>처리일시</label>
                                            <div className="fw-bold" style={{ fontSize: '15px' }}>{toKST(selectedHistory.created_at)}</div>
                                        </div>
                                        
                                        <div className="col-md-6">
                                            <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>결제 금액</label>
                                            <div className="fw-bold text-primary" style={{ fontSize: '15px' }}>
                                                {selectedHistory.payment_amount ? `${Number(selectedHistory.payment_amount).toLocaleString()}원` : '-'}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>등급 변경</label>
                                            <div className="fw-bold" style={{ fontSize: '15px' }}>
                                                {selectedHistory.grade_from && selectedHistory.grade_to ? (
                                                    <>{selectedHistory.grade_from} <i className="fas fa-arrow-right mx-1 text-muted"></i> {selectedHistory.grade_to}</>
                                                ) : '-'}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>영수증</label>
                                            <div className="fw-bold">
                                                {selectedHistory.receipt_url ? (
                                                    <button 
                                                        type="button" 
                                                        onClick={() => window.open(selectedHistory.receipt_url, 'receiptPopup', 'width=450,height=700,left=200,top=100,scrollbars=yes')} 
                                                        className="btn btn-sm btn-outline-secondary py-0 px-3"
                                                        style={{ borderRadius: '20px' }}
                                                    >
                                                        <i className="fas fa-receipt me-1"></i> 조회
                                                    </button>
                                                ) : '-'}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>이전만료일</label>
                                            <div className="fw-bold" style={{ fontSize: '15px' }}>{toKST(selectedHistory.previous_end_date) || '-'}</div>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>신규만료일</label>
                                            <div className="fw-bold text-success" style={{ fontSize: '15px' }}>{toKST(selectedHistory.new_end_date) || '-'}</div>
                                        </div>

                                        {selectedHistory.memo && (
                                            <div className="col-12">
                                                <label className="form-label text-muted mb-1" style={{ fontSize: '12px' }}>메모</label>
                                                <div className="p-3 bg-light rounded text-dark" style={{ fontSize: '14px', border: '1px solid #dee2e6' }}>
                                                    {selectedHistory.memo}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <hr className="my-4" />
                                    
                                    <div className="bg-light p-3 rounded" style={{ border: '1px solid #e9ecef' }}>
                                        <label className="form-label fw-bold mb-2 text-dark" style={{ fontSize: '13px' }}>정산 처리 정보</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className={`badge ${selectedHistory.is_confirmed ? 'bg-success' : 'bg-warning'} px-2 py-1`}>
                                                {selectedHistory.is_confirmed ? '처리완료' : '처리대기'}
                                            </span>
                                            
                                            {selectedHistory.is_confirmed === 1 && (
                                                <div className="ms-1 d-flex align-items-center flex-wrap gap-2">
                                                    <span className="fw-bold text-dark" style={{ fontSize: '14px' }}>
                                                        {selectedHistory.confirmed_by_name 
                                                            ? `${selectedHistory.confirmed_by_name} (${selectedHistory.confirmed_by_id})` 
                                                            : (selectedHistory.reason?.includes('무통장') ? '관리자 수동 승인' : '시스템 자동 승인')
                                                        }
                                                    </span>
                                                    {selectedHistory.confirmed_at && (
                                                        <span className="text-muted" style={{ fontSize: '13px' }}>
                                                            | {toKST(selectedHistory.confirmed_at)}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowDetailModal(false)}
                                    >
                                        닫기
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show ant-like-modal-backdrop"></div>
                </>
            )}
        </>
    );
};

export { Settlement };
